import { Flex, Heading, Skeleton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { MachineContext } from 'contexts/machine.context';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { DaemonName } from 'lib_ts/interfaces/i-machine-daemons';
import { useContext, useEffect, useState } from 'react';
import { MainService } from 'services/main.service';

interface ITestImages {
  machineProcess: DaemonName | 'unknown';
  urls: string[];
}

const TestImages = ({ images }: { images: ITestImages }) => (
  <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
    {images.machineProcess !== 'unknown' && (
      <Heading size="3">{images.machineProcess}</Heading>
    )}

    {images.urls.length === 0 ? (
      <CommonCallout
        text_md={`No images provided by **${images.machineProcess}** health check`}
        color={RADIX.COLOR.DANGER}
      />
    ) : (
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        {images.urls.map((url) => (
          <img key={url} src={url} />
        ))}
      </Flex>
    )}
  </Flex>
);

export const ImagesTab = () => {
  const { healthCheckResults } = useContext(MachineContext);

  const [testImages, setTestImages] = useState<ITestImages[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Legacy health checks only supported a single image via the s3_path field.
   * Newer health checks support multiple images per machine process via the s3_image_paths field.
   *
   * This effect handles both cases to gather all image URLs for signing.
   */
  useEffect(() => {
    const images: ITestImages[] = [];

    if (!healthCheckResults) {
      return;
    }

    // legacy single image path
    if (healthCheckResults.s3_path) {
      images.push({
        machineProcess: 'unknown',
        urls: [healthCheckResults.s3_path],
      });
    }

    // new multiple image paths ({ [MachineProcess]: string[] })
    if (healthCheckResults.s3_image_paths) {
      const s3Paths = Object.entries(healthCheckResults.s3_image_paths);
      for (const [process, urls] of s3Paths) {
        images.push({
          machineProcess: process as DaemonName,
          urls,
        });
      }
    }

    const signUrls = async () => {
      setLoading(true);

      try {
        const urlDict = await MainService.getInstance().signUrls(
          images.flatMap((i) =>
            i.urls.map((path) => ({
              region: AwsRegion.CA,
              path: path,
            }))
          )
        );

        const signedImages = images.map((img) => ({
          ...img,
          urls: img.urls.map((url) => urlDict[url]?.url || url),
        }));

        setTestImages(signedImages);
      } catch (error) {
        NotifyHelper.error({
          message_md: 'Failed to load health check images',
        });

        setTestImages([]);
      } finally {
        setLoading(false);
      }
    };

    signUrls();
  }, [healthCheckResults?.s3_path, healthCheckResults?.s3_image_paths]);

  const noResults = !loading && testImages.length === 0;

  return (
    <Skeleton loading={loading} minHeight="400px">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        {testImages.map((img) => (
          <TestImages key={img.machineProcess} images={img} />
        ))}

        {noResults && (
          <CommonCallout
            text="No images provided by Health Check"
            color={RADIX.COLOR.DANGER}
          />
        )}
      </Flex>
    </Skeleton>
  );
};
